import React, { FC, useMemo } from 'react';
import styles from '../journey-explorer.module.scss';
import {
	FlowDirection,
	JourneyAttribute,
	JourneyFilterState,
	JourneyFlowResult,
	JourneyMeta,
} from '../../../../models/journey-explorer/journey-explorer';
import { GoalInfo, formatNumber, goalRatio } from '../journey-explorer-utils';
import { FlowNodeLayout, isInteractionNode, stepTitle, stepTotal } from '../journey-flow-layout';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import { flowNodeSegment, isEndNode, isTimeable } from '../journey-segments';
import TimeBetween from './time-between';
import ExportCustomers from './export-customers';
import { Dot } from './filter-group';
import { DrawerView } from './explore-drawer';
import SegmentBreakdown from './segment-breakdown';
import CompareStats from './compare-stats';

const HIGH_RATIO = 1.5;
const LOW_RATIO = 0.75;

interface FlowNodePanelProps {
	flow: JourneyFlowResult;
	selected: FlowNodeLayout;
	lookup: Map<string, InteractionInfo>;
	goal: GoalInfo | null;
	meta: JourneyMeta;
	filters: JourneyFilterState;
	onClose: () => void;
	onExplore: (anchor: string, direction: FlowDirection) => void;
	onOpenDrawer: (view: DrawerView) => void;
	onKeep: (attribute: JourneyAttribute, value: string) => void;
	compareLabels: [string, string] | null;
}

const FlowNodePanel: FC<FlowNodePanelProps> = ({
	flow,
	selected,
	lookup,
	goal,
	meta,
	filters,
	onClose,
	onExplore,
	onOpenDrawer,
	onKeep,
	compareLabels,
}) => {
	const { node, label, color } = selected;
	const segment = useMemo(() => flowNodeSegment(flow, node), [flow, node]);
	const explorable = isInteractionNode(node) ? node.name : null;
	const endNode = isEndNode(node);
	let parentTotal = stepTotal(flow, node.step - 1);
	if (node.step === 0) {
		parentTotal = node.customers;
	} else if (endNode) {
		parentTotal = flow.customers;
	}
	const ratio = goal ? goalRatio(node.reached_pct, goal) : 0;
	const anchorLabel = resolveInfo(lookup, flow.anchor).label;
	const endLabel = resolveInfo(lookup, flow.end ?? '').label;
	let subtitle = `${stepTitle(node.step, flow)} ${anchorLabel}`;
	if (node.step === 0) {
		subtitle = stepTitle(0, flow);
	} else if (endNode) {
		subtitle = `Customers who started at ${anchorLabel}`;
	}
	const verdict = (target: GoalInfo) => {
		if (ratio >= HIGH_RATIO) {
			return (
				<>
					Customers on this branch reach {target.label} at <b>{ratio.toFixed(1)}×</b> the average.
				</>
			);
		}
		if (ratio <= LOW_RATIO) {
			return `Customers on this branch reach ${target.label} less often than average: ${node.reached_pct.toFixed(
				0
			)}% against ${target.rate.toFixed(1)}%.`;
		}
		return `The share reaching ${target.label} on this branch is close to the average.`;
	};

	return (
		<div className={styles.panel_body}>
			<div className={styles.panel_header}>
				{isInteractionNode(node) && <Dot color={color} size={8} />}
				<span className={styles.panel_title}>{label}</span>
				<button type="button" className={styles.panel_close} aria-label="Close" onClick={onClose}>
					✕
				</button>
			</div>
			<div className={styles.panel_subtitle}>{subtitle}</div>
			<div className={styles.panel_stats}>
				<div className={styles.panel_stat}>
					<div className={styles.panel_stat_label}>Customers</div>
					<div className={styles.panel_stat_value}>{formatNumber(node.customers)}</div>
					<div className={styles.panel_stat_caption}>
						{parentTotal ? ((node.customers / parentTotal) * 100).toFixed(0) : 0}%{' '}
						{endNode ? 'of starting customers' : 'of the step'}
					</div>
				</div>
				{goal && (
					<div className={styles.panel_stat}>
						<div className={styles.panel_stat_label}>Reached {goal.label}</div>
						<div className={`${styles.panel_stat_value} ${styles.goal_text}`}>
							{node.reached_pct.toFixed(0)}%
						</div>
						<div className={styles.panel_stat_caption}>
							{ratio.toFixed(1)}× the {goal.rate.toFixed(1)}% average
						</div>
					</div>
				)}
			</div>
			{compareLabels && node.a && node.b && <CompareStats labels={compareLabels} a={node.a} b={node.b} />}
			{goal && !goal.outcome && (
				<div className={`${styles.panel_note} ${ratio <= LOW_RATIO ? styles.panel_note_alert : ''}`}>
					{verdict(goal)}
				</div>
			)}
			<div className={styles.panel_actions}>
				{explorable && (
					<>
						<button
							type="button"
							className={styles.panel_action}
							onClick={() => onExplore(explorable, 'after')}
						>
							See what happens after {label}
						</button>
						<button
							type="button"
							className={styles.panel_action}
							onClick={() => onExplore(explorable, 'before')}
						>
							See what leads to {label}
						</button>
					</>
				)}
				<button
					type="button"
					className={styles.panel_action}
					onClick={() => onOpenDrawer({ mode: 'customers', segment, title: label })}
				>
					See customers
				</button>
				<ExportCustomers filters={filters} segment={segment} customers={node.customers} />
			</div>
			{isTimeable(segment) && (
				<>
					<div className={styles.panel_section}>
						Time from {anchorLabel} to {endLabel}
					</div>
					<TimeBetween filters={filters} segment={segment} />
				</>
			)}
			<div className={styles.panel_section}>Who are they?</div>
			<SegmentBreakdown meta={meta} filters={filters} segment={segment} onKeep={onKeep} />
		</div>
	);
};

export default FlowNodePanel;
